<template>
  <SeranovaModal
    data-cy="payment-method-modal"
    size="large"
    body-padding="none"
    show-close-button
    :model-value="true"
    @update:model-value="(v) => !v && $emit('hide-modal')"
  >
    <template #header>
      <div
        class="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm text-gray-700"
        data-cy="payment-method-modal-title-row"
      >
        <strong class="font-semibold text-gray-900">Payment Method</strong>
        <span v-if="customerLabel" class="text-gray-400">·</span>
        <span v-if="customerLabel">{{ customerLabel }}</span>
      </div>
    </template>

    <div
      class="paymentMethodModal flex flex-col min-w-0 divide-y divide-gray-200 [&>*]:px-5 [&>*]:py-3"
    >
      <Spinner v-if="loadingMethods" message="Loading payment methods…" />

      <template v-else-if="customer.payment_method">
        <section data-cy="payment-method-modal-existing">
          <div class="block text-sm font-medium text-gray-900 mb-2">
            On file
          </div>
          <CustomerPaymentMethods
            :customer="customer"
            @payment-method-removed="handleRemove"
          />
        </section>
      </template>

      <template v-else-if="paymentMethods.length">
        <section data-cy="payment-method-modal-method-section">
          <div class="block text-sm font-medium text-gray-900 mb-2">Method</div>
          <MethodTileGroup
            v-model="methodType"
            :methods="methodTiles"
            data-cy="payment-method-modal-methods"
            aria-label="Payment method"
          />
        </section>

        <section v-if="methodType" data-cy="payment-method-modal-form-section">
          <GPICardForm
            v-if="methodType === 'card' && gpiEnabled"
            ref="form"
            auto-save-card
            :customer="customer"
          />
          <StaxCardForm
            v-if="methodType === 'card' && staxEnabled"
            ref="form"
            auto-save-card
            :customer="customer"
          />
          <ACHForm
            v-if="methodType === 'ach' && gpiAchEnabled"
            ref="achForm"
            :form="achFields"
            auto-save-card
          />
          <StaxAchForm
            v-if="methodType === 'ach' && staxAchEnabled"
            ref="achForm"
            :form="achFields"
            :customer="customer"
            auto-save-card
          />
        </section>
      </template>

      <section v-else data-cy="payment-method-modal-empty">
        <div class="text-sm text-gray-600">No Payment Methods Available</div>
      </section>
    </div>

    <template v-if="!customer.payment_method && paymentMethods.length" #footer>
      <StickyConfirmFooter
        tone="pay"
        hide-amount
        verb-override="Save"
        saving-text="Saving…"
        :saving="saving"
        :disabled="!methodType"
        @submit="handleSubmit"
        @cancel="$emit('hide-modal')"
      />
    </template>
  </SeranovaModal>
</template>

<script>
import { mapActions } from "vuex";
import { EventBus } from "@/lib/event_bus.js";

import MethodTileGroup from "@/components/PaymentModals/MethodTileGroup.vue";
import StickyConfirmFooter from "@/components/PaymentModals/StickyConfirmFooter.vue";
import SeranovaModal from "@/components/Seranova/Modal/Modal.vue";
import Spinner from "@/components/Spinner.vue";
import ACHForm from "@/AcceptPayment/ACHForm.vue";
import CustomerPaymentMethods from "@/AcceptPayment/CustomerPaymentMethods.vue";
import GPICardForm from "@/AcceptPayment/GPICardForm.vue";
import StaxAchForm from "@/AcceptPayment/StaxAchForm.vue";
import StaxCardForm from "@/AcceptPayment/StaxCardForm.vue";
import usePaymentMethods from "@/AcceptPayment/usePaymentMethods";
import useVuelidate from "@vuelidate/core";

export default {
  components: {
    ACHForm,
    CustomerPaymentMethods,
    GPICardForm,
    MethodTileGroup,
    SeranovaModal,
    Spinner,
    StaxAchForm,
    StaxCardForm,
    StickyConfirmFooter
  },
  props: {
    customer: {
      type: Object,
      default: null
    }
  },
  emits: ["hide-modal", "save-success"],
  setup() {
    const {
      fetchPaymentMethods,
      fetchCustomer: fetchCustomerForPaymentMethods,
      paymentMethodsResponse
    } = usePaymentMethods();
    return {
      fetchCustomerForPaymentMethods,
      fetchPaymentMethods,
      paymentMethodsResponse,
      v$: useVuelidate()
    };
  },
  data() {
    return {
      loadingMethods: true,
      deleting: false,
      saving: false,
      methodType: "card",
      achFields: {
        transaction_type: "ach",
        ach_first_name: this.customer.first_name,
        ach_last_name: this.customer.last_name,
        ach_account_type: "checking",
        ach_account_number: "",
        ach_routing_number: "",
        ach_save_payment_method: true
      }
    };
  },
  computed: {
    customerLabel() {
      const c = this.customer;
      if (!c) return "";
      return (
        c.full_name || [c.first_name, c.last_name].filter(Boolean).join(" ")
      );
    },
    paymentMethods() {
      return this.paymentMethodsResponse.map((method) => {
        if (method.key === "payment_method") {
          return {
            ...method,
            value: method.key,
            disabled: !this.customer.payment_method
          };
        }
        return { ...method, value: method.key };
      });
    },
    gpiEnabled() {
      return this.paymentMethods.some((method) => method.key === "gpi_card");
    },
    staxEnabled() {
      return this.paymentMethods.some((method) => method.key === "stax_card");
    },
    ccEnabled() {
      return this.paymentMethods.some((method) =>
        ["gpi_card", "stax_card"].includes(method.key)
      );
    },
    achEnabled() {
      return this.paymentMethods.some((method) =>
        ["gpi_ach", "stax_ach"].includes(method.key)
      );
    },
    gpiAchEnabled() {
      return this.paymentMethods.some((method) => method.key === "gpi_ach");
    },
    staxAchEnabled() {
      return this.paymentMethods.some((method) => method.key === "stax_ach");
    },
    methodTiles() {
      // MethodTileGroup keys tiles by `id` (matches modelValue). Disabled
      // when the tenant has no processor configured for that type.
      return [
        {
          id: "card",
          label: "Credit Card",
          icon: "fa-regular fa-credit-card",
          disabled: !this.ccEnabled
        },
        {
          id: "ach",
          label: "ACH",
          icon: "fa-regular fa-building-columns",
          disabled: !this.achEnabled
        }
      ];
    }
  },
  async mounted() {
    this.refreshData();
  },
  methods: {
    ...mapActions(["confirmDelete", "createPaymentMethod"]),
    async refreshData() {
      this.loadingMethods = true;
      try {
        await this.fetchCustomerForPaymentMethods(this.customer.id);
        await this.fetchPaymentMethods();
      } catch (e) {
        // Do nothing
      }
      this.loadingMethods = false;
    },
    handleRemove() {
      window.notify.success("Payment method removed successfully!");
      this.$emit("save-success");
      this.$emit("hide-modal");

      // for any other components
      EventBus.$emit("payment-method-removed");
    },
    handleSubmit() {
      this.saving = true;
      let getPayload = this.submitACH;
      if (this.methodType === "card") {
        if (this.staxEnabled) {
          getPayload = this.submitStax;
        }
        if (this.gpiEnabled) {
          getPayload = this.submitGPI;
        }
      }
      // Get payload from processor form and submit create request
      getPayload()
        .then((params) => {
          return this.createPaymentMethod({
            url: this.customer.payment_method_create_url,
            customer_id: this.customer.id,
            ...params
          }).then(() => {
            window.notify.success("Payment method added successfully!");
            this.$emit("save-success");
            this.$emit("hide-modal");

            EventBus.$emit("payment-method-updated");
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    submitStax() {
      return new Promise((resolve, reject) => {
        return this.$refs.form
          .getData()
          .then((cardData) => {
            resolve({ ...cardData, transaction_type: "card" });
          })
          .catch((err) => reject(err));
      });
    },
    submitGPI() {
      return new Promise((resolve, reject) => {
        this.$refs.form
          .getData()
          .then((cardData) => {
            resolve({ ...cardData, transaction_type: "card" });
          })
          .catch(() => {
            reject();
          });
      });
    },
    async submitACH() {
      const getData = this.$refs.achForm ? this.$refs.achForm.getData : null;
      if (getData) {
        return getData();
      }

      window.notify.close();
      const validated = await this.v$.$validate();
      return new Promise((resolve, reject) => {
        if (validated) {
          this.saving = true;
          resolve(this.achFields);
        } else {
          reject();
        }
      });
    }
  }
};
</script>
